import { OffboardingRecord, OffboardingStatus } from '@/features/offboarding/offboarding.def.ts'
import { useGetAllChecklistConfigsQuery, useGetChecklistByIdQuery } from '@/features/checklist/checklist.api.ts'
import { ChecklistDetails, ChecklistDetailsProvider } from '@/pages/checklist/components/ChecklistDetails.tsx'
import { handleApiError } from '@/utils/api.tsx'
import { Alert, Button, Flex, Form, notification, Space, Spin, Typography } from 'antd'
import React from 'react'
import { useCreateOffboardingChecklistMutation } from '@/features/offboarding/offboarding.api.ts'
import { AntdForm } from '@/common/components/AntdForm'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'

type ChecklistInfoProps = {
  offboardingRecord: OffboardingRecord
}

type CreateChecklistFormValues = {
  checklistConfigId: number
}

const ChecklistInfo: React.FC<ChecklistInfoProps> = ({ offboardingRecord }) => {
  const hasChecklist = !!offboardingRecord?.checklistId
  const { data: checklist, isFetching: checklistFetching } = useGetChecklistByIdQuery(
    { checklistId: offboardingRecord.checklistId as number },
    { skip: !hasChecklist },
  )
  const { data: configs, isFetching: configsFetching } = useGetAllChecklistConfigsQuery(undefined, {
    skip: hasChecklist,
  })
  const [createChecklistForOffboarding, { isLoading: isCreating }] = useCreateOffboardingChecklistMutation()
  const [form] = Form.useForm<CreateChecklistFormValues>()
  const isInProgress = offboardingRecord.status === OffboardingStatus.InProgress

  const checklistConfigOptions = (configs ?? []).filter(config => {
    const sanitizedName = config.name.toLowerCase()
    return !!sanitizedName.includes('offboarding')
  }).map((config) => ({
    label: config.name,
    value: config.id.toString(),
  }))

  const handleCreate = async (values: CreateChecklistFormValues) => {
    try {
      if (!offboardingRecord) return

      await createChecklistForOffboarding({
        offboardingRecordId: offboardingRecord.id,
        checklistConfigId: values.checklistConfigId,
      }).unwrap()

      notification.success({ message: 'Successfully created checklist' })
    } catch (error) {
      handleApiError(error)
    }
  }

  if (!offboardingRecord) {
    return <Alert type="warning" showIcon message="Offboarding record not found." />
  }

  return (
    <Flex gap="middle" vertical>
      {!hasChecklist ?
        <Spin spinning={checklistFetching || configsFetching}>
          <Space direction="vertical" size="middle" className="w-full max-w-[440px]">
            <Typography>Select a checklist template to create a checklist for the employee.</Typography>
            <Form form={form} layout="vertical" className="w-full" onFinish={handleCreate}>
              <AntdForm.Section>
                <AntdForm.Select
                  fieldProps={{
                    name: 'checklistConfigId',
                    label: 'Checklist Template',
                    rules: [AntdFormRules.required],
                    span: 24,
                  }}
                  disabled={!isInProgress}
                  options={checklistConfigOptions}
                />
              </AntdForm.Section>
              <Button
                htmlType="submit"
                type="primary"
                loading={isCreating}
                disabled={!isInProgress}
              >
                Create Checklist
              </Button>
            </Form>
          </Space>
        </Spin>
        : null}
      {hasChecklist && checklistFetching ? <Spin /> : null}
      {checklist ?
        <Flex gap="small" vertical>
          <ChecklistDetailsProvider data={checklist} key={offboardingRecord.id}>
            <ChecklistDetails />
          </ChecklistDetailsProvider>
        </Flex>
        : null}
    </Flex>
  )
}

export default ChecklistInfo
